import { ReportParameters } from '@/reports/models/reports';
import { ReportIdentifier } from '@/reports/report-constants';
import { addDays, formatDateForApi } from '@/date-time/date-time-utils';
import { ReportDateFiltersService } from '@/reports/report-date-filters-service';

export class ReportFilterUtils {
    private static readonly DEFAULT_DATE_RANGE_DAYS = 31;
    private static readonly DEFAULT_END_DATE_OFFSET = 1;

    private static reportDateFilterService = new ReportDateFiltersService();

    /**
     * Get initial report parameters based on preFilterReports setting
     *
     * @param preFilterEnabled - Whether pre-filtering is enabled
     * @param activeReport - Optional active report identifier for date field
     * @returns Initial report parameters
     */
    static getInitialReportParameters(
        preFilterEnabled: boolean,
        activeReport?: ReportIdentifier
    ): ReportParameters {
        if (!preFilterEnabled) {
            return {};
        }

        const params: ReportParameters = {
            start_date: formatDateForApi(addDays(new Date(), -this.DEFAULT_DATE_RANGE_DAYS)),
            end_date: formatDateForApi(addDays(new Date(), -this.DEFAULT_END_DATE_OFFSET))
        };

        if (activeReport) {
            params.date_field = this.reportDateFilterService.getDefaultFilter(activeReport);
        }

        return params;
    }

    /**
     * Update report parameters based on preFilterReports setting
     *
     * @param enabled - Whether pre-filtering is enabled
     * @param activeReport - Optional active report identifier for date field
     * @returns Updated report parameters
     */
    static updateParametersBasedOnSetting(
        enabled: boolean,
        activeReport?: ReportIdentifier
    ): ReportParameters {
        if (!enabled) {
            return {};
        }

        const params = this.getInitialReportParameters(enabled, activeReport);

        if (activeReport === ReportIdentifier.LOCATIONS) {
            params.start_date = '';
            params.end_date = '';
        }

        return params;
    }

    /**
     * Check if a report should be date filterable based on report type
     *
     * @param activeReport - The active report identifier
     * @returns Whether the report supports date filtering
     */
    static isReportDateFilterable(activeReport: ReportIdentifier): boolean {
        const nonFilterableReports = [
            ReportIdentifier.LOCATIONS,
            ReportIdentifier.STAFF,
            ReportIdentifier.MARKETING_CAMPAIGNS
        ];

        return !nonFilterableReports.includes(activeReport);
    }

    /**
     * Get default date range for reports (last 31 days)
     *
     * @returns Object with start and end dates
     */
    static getDefaultDateRange(): { start_date: string; end_date: string } {
        return {
            start_date: formatDateForApi(addDays(new Date(), -this.DEFAULT_DATE_RANGE_DAYS)),
            end_date: formatDateForApi(addDays(new Date(), -this.DEFAULT_END_DATE_OFFSET))
        };
    }

    /**
     * Check if report parameters have valid date range
     *
     * @param params - Report parameters to validate
     * @returns Whether the parameters have a valid date range
     */
    static hasValidDateRange(params: ReportParameters): boolean {
        return Boolean(params.start_date && params.end_date);
    }

    /**
     * Clear date parameters from report parameters
     *
     * @param params - Report parameters to clear
     * @returns Cleaned parameters without date fields
     */
    static clearDateParameters(params: ReportParameters): ReportParameters {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { start_date, end_date, date_field, ...cleanedParams } = params;
        return cleanedParams;
    }
}
